import { CheckIcon } from '@heroicons/react/20/solid';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useState } from 'react';
import { LoadingSpinner } from '~/components';
import { CostCalculatorFailureNotice } from '~/features/market/listings/components/CostCalculatorFailureNotice';
import {
  formatCalculatedMoney,
  hasValidCostCalculation,
} from '~/features/market/listings/components/costCalculatorMoney';
import { B2B_LISTING_FEE_NOTE } from '~/features/market/listings/components/feeDisclosure';
import { formatSubspecialties } from '~/features/market/listings/subspecialties';
import { MarketAlert } from '~/features/shared/market-content';
import { dateStringFormatter } from '~/features/utils';
import { loadCategoryOptions } from '~/features/vault/profile/api';
import { InfoSection } from '../../../components/InfoSection';
import { selectWizardListingSide } from '../../listingSide';
import { useMsaasListingsActorRef } from '../../msaasProvider';
import type { Context as MsaasListingContext } from '../../types';
import { coversPlace, coversStates } from '../offering-coverage/types';
import { geographyToLocationOption, WORK_LOCATION_NAME } from '../offering-coverage/utils';
import { formatShiftDistributionStr } from './util';

const TAGS = CT.employer.msaasListings.reviewInfo;

// [MCV-1889] The roster is optional, but it is what lets buyers verify the license states.
export const ROSTER_WARNING = 'Add providers to verify your license states and raise your match.';

const CoverageReview = ({
  listing,
  rosterCount,
}: {
  listing: NonNullable<MsaasListingContext['listing']>;
  rosterCount: number | undefined;
}) => {
  const place = geographyToLocationOption(listing.geographies?.[0]);
  const radius = listing.geographies?.[0]?.radiusMiles;
  return (
    <>
      <InfoSection
        title="Coverage"
        infoList={[
          {
            label: 'Work Location',
            value: WORK_LOCATION_NAME[listing.workLocation] ?? listing.workLocation,
            cyTag: 'review-work-location',
          },
          ...(coversStates(listing.workLocation)
            ? [
                {
                  label: 'License States',
                  value: listing.stateCodes?.length
                    ? listing.stateCodes.join(', ')
                    : 'Not Provided',
                  cyTag: 'review-license-states',
                },
              ]
            : []),
          ...(coversPlace(listing.workLocation)
            ? [
                {
                  label: 'Coverage Area',
                  value: place ? `${radius} mi from ${place.label}` : 'Not Provided',
                  cyTag: 'review-coverage-area',
                },
              ]
            : []),
          ...(rosterCount != null
            ? [
                {
                  label: 'Offered Providers',
                  value: String(rosterCount),
                  cyTag: 'review-roster-count',
                },
              ]
            : []),
        ]}
      />
      {rosterCount === 0 && (
        <div data-cy="review-roster-warning">
          <MarketAlert tone="warning">{ROSTER_WARNING}</MarketAlert>
        </div>
      )}
    </>
  );
};

export const ReviewInfo = ({ footer }: { footer: React.ReactNode }) => {
  const msaasListingsRef = useMsaasListingsActorRef();
  const msaasListing = useSelector(msaasListingsRef, (s): MsaasListingContext['listing'] => {
    if (typeof s === 'object' && s !== null && 'context' in s) {
      return (s.context as MsaasListingContext).listing;
    }
    throw new Error('Invalid state: context not found');
  });
  const [providerCategory, setProviderCategory] = useState<string>('');
  const summary = useSelector(msaasListingsRef, (state) => state.context.summary);
  // [MCV-1889] A selling listing reviews its coverage and roster instead of facilities.
  const isOffering = useSelector(
    msaasListingsRef,
    (state) => selectWizardListingSide(state.context) === 'OFFERING'
  );
  const rosterProviderIds = useSelector(
    msaasListingsRef,
    (state) => state.context.rosterProviderIds
  );

  useEffect(() => {
    const getProviderCategory = async () => {
      const options = await loadCategoryOptions();
      if (!msaasListing) return;
      const providerCategory = options.find(
        (option) => option.value === msaasListing.providerCategoryId
      );
      if (!providerCategory) return;
      setProviderCategory(providerCategory.label);
    };
    getProviderCategory();
  }, [msaasListing]);

  if (!msaasListing?.detail || !providerCategory) {
    return <LoadingSpinner />;
  }
  const hasValidCalculation = hasValidCostCalculation(msaasListing.detail);
  const subspecialties = formatSubspecialties(msaasListing);

  return (
    <div className="mt-8">
      <InfoSection
        title="Basic Info"
        infoList={[
          { label: 'Title', value: msaasListing.listingTitle, cyTag: TAGS.title },
          { label: 'Provider Category', value: providerCategory, cyTag: TAGS.providerCategory },
          {
            label: 'Specialty',
            value: msaasListing.specialtyName || 'Not Provided',
            cyTag: TAGS.specialty,
          },
          ...(subspecialties
            ? [{ label: 'Subspecialties', value: subspecialties, cyTag: TAGS.subspecialties }]
            : []),
          {
            label: 'Renewable',
            value: msaasListing.isRenewable ? 'Yes' : 'No',
            cyTag: TAGS.renewable,
          },
        ]}
      />
      {isOffering && (
        <CoverageReview listing={msaasListing} rosterCount={rosterProviderIds?.length} />
      )}
      <InfoSection
        title="Labor Metrics"
        infoList={[
          {
            label: 'Contract Period',
            value: `${dateStringFormatter(msaasListing.detail.startDate)} - ${
              msaasListing.detail.endDate
                ? dateStringFormatter(msaasListing.detail.endDate)
                : 'Not Provided'
            }`,
            cyTag: TAGS.startDate,
          },
          {
            label: 'Number of Providers (FTEs)',
            value: msaasListing.detail.numOfFtes?.toString() ?? 'Not Provided',
            cyTag: TAGS.numOfFtes,
          },
          {
            label: 'Total Shifts',
            value: msaasListing.detail.fteShiftsPerYear?.toString() ?? 'Not Provided',
            cyTag: TAGS.totalShifts,
          },
          {
            label: 'Total Hours',
            value:
              msaasListing.detail.hoursPerShift !== null &&
              msaasListing.detail.fteShiftsPerYear !== null
                ? `${msaasListing.detail.hoursPerShift * msaasListing.detail.fteShiftsPerYear}`
                : 'Not Provided',
            cyTag: TAGS.totalHours,
          },
        ]}
      />
      <InfoSection
        title="Shift Distribution & Differentials"
        infoList={[
          {
            label: 'Weekday Shifts',
            value: formatShiftDistributionStr(
              msaasListing.detail.numDayShifts,
              msaasListing.detail.numEveShifts,
              msaasListing.detail.numNightShifts
            ),
            cyTag: TAGS.weekdayShifts,
          },
          {
            label: 'Weekend Shifts',
            value: formatShiftDistributionStr(
              msaasListing.detail.numOfWeDayShifts,
              msaasListing.detail.numOfWeEveShifts,
              msaasListing.detail.numOfWeNightShifts
            ),
            cyTag: TAGS.weekendShifts,
          },
          {
            label: 'Holiday Shifts',
            value: formatShiftDistributionStr(
              msaasListing.detail.numHolDayShifts,
              msaasListing.detail.numHolEveShifts,
              msaasListing.detail.numHolNightShifts
            ),
            cyTag: TAGS.holidayShifts,
          },
          {
            label: 'Total Differentials',
            value:
              summary?.shiftDifferentialTotal != null
                ? `$${Number(summary.shiftDifferentialTotal).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                  })}`
                : 'Calculating...',
            cyTag: TAGS.diffCost,
          },
        ]}
      />
      <InfoSection
        title="Compensation Summary"
        infoList={[
          {
            label: 'Compensation Type',
            value:
              msaasListing.detail.compType != null
                ? msaasListing.detail.compType.charAt(0) +
                  msaasListing.detail.compType.slice(1).toLowerCase()
                : 'Not Provided',
            cyTag: TAGS.compType,
          },
          {
            label: 'Average Base Hourly Rate',
            value: formatCalculatedMoney(msaasListing.detail.pricePerHour),
            cyTag: TAGS.avgBaseHourlyRate,
          },
          {
            label: 'Average Hourly Rate with Differentials',
            value: formatCalculatedMoney(msaasListing.detail.avgHourlyRate),
            cyTag: TAGS.avgRateWithDiff,
          },
          {
            label: 'Total Contract Value',
            value: formatCalculatedMoney(msaasListing.detail.totalContractValue),
            cyTag: TAGS.totalContractValue,
          },
        ]}
      />

      {hasValidCalculation ? (
        <div className="w-full bg-success-subtle p-4 rounded-lg mb-4 mt-3">
          <div className="flex items-center mb-3">
            <CheckIcon className="h-5 w-5 text-success stroke-2 mr-3" />
            <p className="body-text-sb">Ready to Submit</p>
          </div>
          <p className="body-text-sb">
            Your B2B listing is complete and ready to be published. Once submitted, it will be
            visible to qualified employers on the platform.
          </p>
          {/* [MCV-1859] Fee disclosure (rate changed to 1.5%, MCV-1949); explicit text-left because
              #root centers text. */}
          <p
            className="mt-3 text-left text-[13px] leading-snug text-grey-300"
            data-cy={TAGS.feeDisclaimer}
          >
            {B2B_LISTING_FEE_NOTE}
          </p>
        </div>
      ) : (
        <div className="mb-4 mt-3">
          <CostCalculatorFailureNotice />
        </div>
      )}
      {footer}
    </div>
  );
};
